<template>
    <basic-header pretitle="Aqfer graph management" title="Cluster Formation" />

    <div class="container-fluid cluster-formation-page mt-3 mb-4">
        <curation-tabs />

        <!-- ── Lookup bar ──────────────────────────────────────────────── -->
        <div class="card border-0 shadow-sm cf-lookup-card">
            <div class="card-body cf-lookup-body">
                <div class="cf-lookup-row">
                    <div class="cf-lookup-field">
                        <label class="cf-field-label" for="cf-entity-input">Entity</label>
                        <div class="cf-input-shell" :class="{ 'is-invalid': entityInput && !parsedEntity }">
                            <i class="fa fa-crosshairs cf-input-icon"></i>
                            <input
                                id="cf-entity-input"
                                v-model="entityInput"
                                type="text"
                                class="cf-input"
                                spellcheck="false"
                                autocomplete="off"
                                placeholder="entity_type~entity_domain~entity_id"
                                @keyup.enter="runLookup"
                            />
                            <button
                                v-if="entityInput"
                                type="button"
                                class="cf-input-clear"
                                title="Clear"
                                @click="entityInput = ''"
                            ><i class="fa fa-xmark"></i></button>
                        </div>
                        <!-- Live parse feedback: the three query parameters, as typed -->
                        <div class="cf-parse-row">
                            <template v-if="parsedEntity">
                                <span class="cf-parse-chip"><i>type</i>{{ parsedEntity.type }}</span>
                                <span class="cf-parse-chip"><i>domain</i>{{ parsedEntity.domain }}</span>
                                <span class="cf-parse-chip cf-parse-chip--id"><i>id</i>{{ parsedEntity.id }}</span>
                            </template>
                            <span v-else-if="entityInput" class="cf-parse-hint cf-parse-hint--warn">
                                <i class="fa fa-triangle-exclamation me-1"></i>Expected three <code>~</code>-separated parts.
                            </span>
                            <span v-else class="cf-parse-hint">
                                Example: <button type="button" class="cf-example" @click="entityInput = exampleEntity">{{ exampleEntity }}</button>
                            </span>
                        </div>
                    </div>

                    <div class="cf-lookup-actions">
                        <button
                            type="button"
                            class="btn btn-primary btn-sm cf-run-btn"
                            :disabled="loading || !parsedEntity"
                            @click="runLookup"
                        >
                            <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" />
                            <i v-else class="fa fa-play me-1" />
                            {{ loading ? 'Running…' : 'Trace formation' }}
                        </button>
                    </div>
                </div>

                <div v-if="recentLookups.length" class="cf-recent-row">
                    <span class="cf-recent-label">Recent</span>
                    <button
                        v-for="r in recentLookups"
                        :key="r"
                        type="button"
                        class="cf-recent-chip"
                        :title="r"
                        @click="entityInput = r; runLookup()"
                    >{{ r }}</button>
                </div>

                <div v-if="error" class="alert alert-danger mt-3 mb-0 py-2 small">
                    <i class="fa fa-circle-exclamation me-1" />{{ error }}
                </div>
            </div>
        </div>

        <!-- ── Empty / loading state ──────────────────────────────────── -->
        <div v-if="loading" class="card border-0 shadow-sm cf-blank-card">
            <div class="card-body cf-blank">
                <div class="spinner-border text-primary mb-3" role="status" />
                <h5>Running curation query…</h5>
                <p>Executing against <code>{{ dataset }}</code> ({{ queryDialect === 'bigquery' ? 'BigQuery' : 'Athena' }}) and polling for results.</p>
            </div>
        </div>

        <div v-else-if="!model" class="card border-0 shadow-sm cf-blank-card">
            <div class="card-body cf-blank">
                <div class="cf-blank-art">
                    <span class="cf-blank-ring cf-blank-ring--2"></span>
                    <span class="cf-blank-ring cf-blank-ring--1"></span>
                    <span class="cf-blank-core"><i class="fa fa-crosshairs"></i></span>
                </div>
                <h5>Trace how a cluster formed</h5>
                <p>
                    Enter an entity as <code>type~domain~id</code>. The page reads left to right:
                    your entity, the cluster it belongs to, then that cluster's neighbours — so you can see
                    exactly what connects your entity to the rest of the cluster. A neighbour your entity
                    <span class="cf-inline-bridge">also has directly</span> is ringed, because that is where
                    the two actually touch.
                </p>
            </div>
        </div>

        <!-- ── Results ────────────────────────────────────────────────── -->
        <template v-else>
            <!-- Stat strip -->
            <div class="cf-stat-strip">
                <div class="cf-stat">
                    <span class="cf-stat-lbl">Root entity</span>
                    <span class="cf-stat-val cf-stat-val--mono" :title="model.rootKey">{{ model.rootKey }}</span>
                </div>
                <div class="cf-stat cf-stat--num">
                    <span class="cf-stat-lbl">Clusters</span>
                    <span class="cf-stat-val">{{ model.stats.clusters }}</span>
                </div>
                <div class="cf-stat cf-stat--num" title="flatten_neighbors_array — the neighbours of the entity you queried">
                    <span class="cf-stat-lbl">Your neighbours</span>
                    <span class="cf-stat-val">{{ model.stats.flatten }}</span>
                </div>
                <div class="cf-stat cf-stat--num" title="final_graph_map — the neighbours of the clusters">
                    <span class="cf-stat-lbl">Cluster neighbours</span>
                    <span class="cf-stat-val">{{ model.stats.uniqueNeighbors }}</span>
                </div>
                <div
                    class="cf-stat cf-stat--num cf-stat--bridge"
                    title="Cluster neighbours that your entity also has directly — where your entity and the cluster touch"
                >
                    <span class="cf-stat-lbl">Also yours</span>
                    <span class="cf-stat-val">{{ model.stats.uniqueShared }}</span>
                </div>
                <div
                    v-if="model.directOnlyCount"
                    class="cf-stat cf-stat--num cf-stat--own"
                    title="Direct neighbours of your entity drawn as separate nodes off it"
                >
                    <span class="cf-stat-lbl">Separate nodes</span>
                    <span class="cf-stat-val">{{ model.directOnlyCount }}</span>
                </div>
            </div>

            <p class="cf-explainer" v-html="explainer"></p>

            <div class="row g-2 cf-bench-row">
                <!-- Breakdown -->
                <div class="col-12 col-lg-4 col-xxl-3 d-flex">
                    <div class="card border-0 shadow-sm w-100 d-flex flex-column cf-side-card">
                        <div class="card-header bg-transparent border-0 cf-side-header">
                            <h5 class="card-title mb-0">Formation</h5>
                            <button
                                v-if="focusCluster"
                                type="button"
                                class="btn btn-sm btn-link p-0 cf-clear-focus"
                                @click="focusCluster = null"
                            >Show all</button>
                        </div>

                        <div class="cf-side-scroll">
                            <!-- Root -->
                            <div class="cf-root-card">
                                <span class="cf-level-tag">Your entity</span>
                                <div class="cf-root-key">{{ model.rootKey }}</div>
                                <div class="cf-root-meta">
                                    <span>{{ model.stats.clusters }} cluster{{ model.stats.clusters === 1 ? '' : 's' }}</span>
                                    <span class="cf-dot-sep">·</span>
                                    <span>{{ model.stats.flatten }} direct neighbour{{ model.stats.flatten === 1 ? '' : 's' }}</span>
                                </div>
                            </div>

                            <div class="cf-side-sec-lbl">
                                Clusters it belongs to
                                <span class="cf-side-sec-cnt">{{ model.clusters.length }}</span>
                            </div>

                            <p v-if="!model.clusters.length" class="cf-side-empty">
                                No clusters returned for this entity.
                            </p>

                            <button
                                v-for="c in model.clusters"
                                :key="c.key"
                                type="button"
                                class="cf-cluster-card"
                                :class="{ 'is-focused': focusCluster === c.key, 'has-bridge': c.sharedCount > 0 }"
                                @click="focusCluster = focusCluster === c.key ? null : c.key"
                            >
                                <div class="cf-cluster-top">
                                    <span class="cf-cluster-td">{{ c.type }}~{{ c.domain }}</span>
                                    <span v-if="c.sharedCount" class="cf-bridge-badge" :title="`${c.sharedCount} neighbour(s) your entity also has`">
                                        <i class="fa fa-link"></i>{{ c.sharedCount }}
                                    </span>
                                </div>
                                <div class="cf-cluster-id">{{ c.id }}</div>
                                <div class="cf-cluster-bar">
                                    <span
                                        class="cf-cluster-bar-fill"
                                        :style="{ width: barWidth(c.neighborCount) }"
                                    ></span>
                                    <span
                                        v-if="c.sharedCount"
                                        class="cf-cluster-bar-bridge"
                                        :style="{ width: barWidth(c.sharedCount) }"
                                    ></span>
                                </div>
                                <div class="cf-cluster-meta">
                                    <span><b>{{ c.neighborCount }}</b> neighbours</span>
                                    <span v-if="c.visibleCount !== c.neighborCount" class="cf-muted">
                                        {{ c.visibleCount }} shown
                                    </span>
                                    <span v-if="c.movedInCount" class="cf-moved-in" title="Moved into this cluster, not saved yet">
                                        +{{ c.movedInCount }} moved
                                    </span>
                                </div>
                            </button>

                            <!-- Neighbours no cluster holds. They are on the
                                 graph as their own branch; listing them here
                                 gives the IDs the nodes only summarise. -->
                            <template v-if="model.directOnlyCount">
                                <div class="cf-side-sec-lbl">
                                    Separate nodes
                                    <span class="cf-side-sec-cnt">{{ model.directOnlyCount }}</span>
                                </div>
                                <div class="cf-own-card">
                                    <p class="cf-own-note">
                                        Direct neighbours of your entity, drawn as separate nodes off it.
                                    </p>
                                    <div class="cf-own-list">
                                        <div v-for="(id, i) in ownPreview" :key="'o' + i" class="cf-own-row">{{ id }}</div>
                                    </div>
                                    <button
                                        v-if="model.directOnly.length > OWN_PREVIEW"
                                        type="button"
                                        class="btn btn-sm btn-link p-0 cf-own-more"
                                        @click="ownShowAll = !ownShowAll"
                                    >{{ ownShowAll ? 'Show fewer' : `Show all ${model.directOnly.length}` }}</button>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Graph -->
                <div class="col-12 col-lg-8 col-xxl-9 d-flex">
                    <div class="card border-0 shadow-sm w-100 d-flex flex-column cf-graph-card">
                        <!-- Graph controls -->
                        <div class="cf-graph-controls">

                            <div class="btn-group btn-group-sm cf-mode-toggle" role="group">
                                <button
                                    type="button"
                                    class="btn"
                                    :class="mode === 'connects' ? 'btn-primary' : 'btn-outline-primary'"
                                    title="Draw only the neighbours your entity also has — the ones that explain the link"
                                    @click="mode = 'connects'"
                                >How it connects</button>
                                <button
                                    type="button"
                                    class="btn"
                                    :class="mode === 'all' ? 'btn-primary' : 'btn-outline-primary'"
                                    title="Draw individual neighbours, shared ones first"
                                    @click="mode = 'all'"
                                >All neighbours</button>
                                <button
                                    type="button"
                                    class="btn"
                                    :class="mode === 'grouped' ? 'btn-primary' : 'btn-outline-primary'"
                                    title="Collapse neighbours into one node per type~domain"
                                    @click="mode = 'grouped'"
                                >By type</button>
                            </div>

                            <div v-if="mode === 'all'" class="cf-ctrl">
                                <span class="cf-ctrl-lbl">Show</span>
                                <input
                                    v-model.number="expandCap"
                                    type="number"
                                    class="form-control form-control-sm cf-cap-input"
                                    min="1"
                                    :max="CAP_MAX"
                                    title="How many neighbours to draw per cluster"
                                />
                            </div>

                            <div class="cf-ctrl cf-ctrl--grow">
                                <i class="fa fa-filter cf-ctrl-icon"></i>
                                <input
                                    v-model="filterInput"
                                    type="text"
                                    class="form-control form-control-sm"
                                    placeholder="Filter neighbours by type, domain or ID…"
                                />
                            </div>

                            <button
                                v-if="canCurate"
                                type="button"
                                class="btn btn-sm cf-move-toggle"
                                :class="moveMode ? 'btn-success' : 'btn-outline-success'"
                                :disabled="!model.clusters.length"
                                title="Drag members onto another cluster to merge them into it"
                                @click="moveMode = !moveMode"
                            ><i class="fa fa-arrows-up-down-left-right me-1"></i>Move nodes</button>

                        </div>

                        <div v-if="moveMode && moveBlockedReason" class="cf-move-hint cf-move-hint--warn">
                            <i class="fa fa-circle-info me-1"></i>{{ moveBlockedReason }}
                        </div>
                        <div v-else-if="moveMode" class="cf-move-hint">
                            <i class="fa fa-hand-pointer me-1"></i>
                            Drag a member, group or bundle onto another cluster and let go when that cluster rings
                            green. Click a group first to open it and move single IDs. Moved nodes get a green ring
                            until you save.
                        </div>

                        <div class="cf-graph-body">
                            <d3-cluster-formation-graph
                                :data="displayGraph"
                                :enable-node-move="canCurate && moveMode"
                                empty-text="Nothing matches the current filters."
                                @node-moved="onNodeMoved"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── Staged moves (merge curation) ─────────────────────── -->
            <div v-if="moves.length" class="card border-0 shadow-sm mt-2 cf-moves-card">
                <div class="card-header bg-transparent cf-moves-header">
                    <h5 class="card-title mb-0">
                        Pending moves
                        <span class="badge bg-success rounded-pill ms-1">{{ moves.length }}</span>
                    </h5>
                    <div class="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            :disabled="savingMoves"
                            @click="clearMoves"
                        ><i class="fa fa-rotate-left me-1"></i>Clear all</button>
                        <button
                            type="button"
                            class="btn btn-sm btn-primary"
                            :disabled="savingMoves"
                            @click="saveMoves"
                        >
                            <span v-if="savingMoves" class="spinner-border spinner-border-sm me-1" role="status" />
                            <i v-else class="fa fa-floppy-disk me-1"></i>Save moves
                        </button>
                    </div>
                </div>
                <div class="cf-moves-scroll">
                    <table class="table table-sm table-hover align-middle mb-0 cf-moves-table">
                        <thead class="table-light">
                            <tr>
                                <th class="ps-3">type~domain~id</th>
                                <th>From</th>
                                <th class="text-center" style="width: 1.5rem">→</th>
                                <th>To</th>
                                <th style="width: 2.5rem"><span class="visually-hidden">Remove</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="mv in moveRows" :key="mv.rowKey">
                                <td class="font-monospace small ps-3"><span class="cf-move-id" :title="mv.key">{{ mv.key }}</span></td>
                                <td class="small">
                                    <span class="cf-move-chip" :class="{ 'cf-move-chip--none': !mv.from }" :title="mv.from || 'Separate node'">{{ mv.fromLabel }}</span>
                                </td>
                                <td class="text-center small text-muted">→</td>
                                <td class="small"><span class="cf-move-chip cf-move-chip--to" :title="mv.to">{{ mv.toLabel }}</span></td>
                                <td class="text-center">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-link text-muted p-0"
                                        title="Undo this move"
                                        :disabled="savingMoves"
                                        @click="removeMove(mv)"
                                    ><i class="fa fa-xmark"></i></button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios'
import BasicHeader from '../../components/BasicHeader.vue'
import CurationTabs from '../../components/CurationTabs.vue'
import D3ClusterFormationGraph from '../../components/D3ClusterFormationGraph.vue'
import {
    addClusterMove,
    buildClusterFormationCurationPayload,
    buildClusterFormationGraph,
    clusterIdOfKey,
    clusterMembership,
    NEIGHBOR_EXPAND_CAP,
    NEIGHBOR_EXPAND_CAP_MAX,
} from '../../utils/clusterFormation'

const MAX_POLL = 60
const POLL_MS = 1000
const RECENT_KEY = 'agm.clusterFormation.recent'
const RECENT_MAX = 5
/* IDs shown under "Separate nodes" before the list has to be expanded. */
const OWN_PREVIEW = 20

/* Dataset and tables are fixed for now; only the three lookup params vary. */
const DATASET_BY_DIALECT = { athena: 'development', bigquery: 'aqfer_v1' }

/**
 * The query is written in the dialect of the cloud Pail runs on — BigQuery on
 * GCP, Athena everywhere else — and the dialect is declared to Pail so it runs
 * verbatim instead of being translated.
 *
 * pail/server/internal/service/query_v2.go, queryStarterV2:
 *     srcDialect := rc.QueryParams["source_dialect"]
 *     if srcDialect == "" { srcDialect = dialectAthena }
 *
 * It is a query-string parameter, not a body field — QueryStarterBody carries
 * only `query`. executeQueryV2 then translates only when srcDialect differs
 * from the metastore's own dialect. Sending BigQuery SQL undeclared is what
 * produced "SQL translation (athena -> bigquery) failed".
 */
const DIALECT_ATHENA = 'athena'
const DIALECT_BIGQUERY = 'bigquery'
const GCP_CLOUD_KEYS = ['gcp', 'google', 'gcloud']

/** Athena/BigQuery string literal — the only user-controlled part of the query. */
function lit(v) {
    return `'${String(v == null ? '' : v).replace(/'/g, "''")}'`
}

export default {
    name: 'ClusterFormation',
    components: { BasicHeader, CurationTabs, D3ClusterFormationGraph },
    data() {
        return {
            CAP_MAX: NEIGHBOR_EXPAND_CAP_MAX,
            exampleEntity: 'em~raw~001326david@gmail.com',
            entityInput: '',
            loading: false,
            error: null,
            row: null,
            recentLookups: [],

            /* 'connects' answers the question the page exists for; the other
               two are drill-downs. */
            mode: 'grouped',
            ownShowAll: false,
            OWN_PREVIEW,
            expandCap: 12,
            filterInput: '',
            filter: '',
            focusCluster: null,
            filterTimer: null,

            /* Merge curation: members dragged between clusters, as { key, from, to }. */
            moveMode: false,
            moves: [],
            savingMoves: false,
        }
    },
    computed: {
        cid() {
            return this.$route.params.cid || this.$store.state.currentClient?.cid || ''
        },
        parsedEntity() {
            let raw = String(this.entityInput || '').trim()
            if (!raw) return null
            /* Tolerate a pasted label — "entity_type: ck~nypost~abc" — by dropping
               anything up to the last colon that sits before the first tilde. */
            const firstTilde = raw.indexOf('~')
            const head = firstTilde === -1 ? raw : raw.slice(0, firstTilde)
            const colon = head.lastIndexOf(':')
            if (colon !== -1) raw = raw.slice(colon + 1)

            const parts = raw.split('~')
            if (parts.length < 3) return null
            const type = parts[0].trim()
            const domain = parts[1].trim()
            const id = parts.slice(2).join('~').trim()
            if (!type || !domain || !id) return null
            /* A newline inside a literal would break the single-line query we send. */
            if ([type, domain, id].some((v) => /[\r\n]/.test(v))) return null
            return { type, domain, id }
        },
        /**
         * Cloud of the deployment the current Pail URL came from. A cloud with
         * no pail endpoint of its own falls back to the default (AWS) Pail, so
         * the selected cloud alone is not enough — find who serves pailUrl.
         */
        pailCloud() {
            const { pailUrl, selectedCloud, endpointsByCloudRegion } = this.$store.state
            const byCloud = endpointsByCloudRegion || {}
            const owner = Object.keys(byCloud).find((cloud) =>
                Object.values(byCloud[cloud] || {}).some((endpoints) => endpoints && endpoints.pail === pailUrl)
            )
            if (owner != null) return owner
            return pailUrl === this.$store.state.defaultUrls.pail ? 'aws' : String(selectedCloud || '')
        },
        queryDialect() {
            return GCP_CLOUD_KEYS.includes(this.pailCloud) ? DIALECT_BIGQUERY : DIALECT_ATHENA
        },
        /** Athena and BigQuery keep the curation tables in different databases. */
        dataset() {
            return DATASET_BY_DIALECT[this.queryDialect]
        },
        tables() {
            return {
                chooseCluster: `${this.dataset}.agm_curation_choose_cluster`,
                finalCuration: `${this.dataset}.relaxed_curation_final_graph`,
                flatten: `${this.dataset}.agm_curation_flatten`,
            }
        },
        /**
         * The query, with `@T` / `@D` / `@I` standing in for the three lookup
         * params. Kept literal-free so whitespace can be collapsed safely.
         */
        sqlTemplate() {
            return this.queryDialect === DIALECT_BIGQUERY ? this.sqlTemplateBigQuery : this.sqlTemplateAthena
        },
        /**
         * Athena (Trino) form. In these tables the cluster side names its
         * neighbour columns entity_neighbor_*, and the flatten table's id column
         * is entity_neighbor_id — both differ from the BigQuery tables.
         */
        sqlTemplateAthena() {
            return `WITH valid_cluster_data AS (
    SELECT
        @T AS entity_type,
        @D AS entity_domain,
        @I AS entity_id,

        ARRAY_AGG(CONCAT(c.cluster_type, '~', c.cluster_domain, '~', CAST(t.single_cluster_id AS VARCHAR))) AS cluster_list,

        ARRAY_AGG(
            CAST(
                ROW(
                    CONCAT(c.cluster_type, '~', c.cluster_domain, '~', CAST(t.single_cluster_id AS VARCHAR)),
                    transform(
                        COALESCE(fc.entity_neighbors, ARRAY[]),
                        n -> CONCAT(fc.entity_neighbor_type, '~', fc.entity_neighbor_domain, '~', CAST(n.id AS VARCHAR))
                    )
                ) AS ROW(key VARCHAR, value ARRAY(VARCHAR))
            )
        ) AS final_graph_map

    FROM ${this.tables.chooseCluster} c
    CROSS JOIN UNNEST(COALESCE(c.clusters, ARRAY[])) AS t(single_cluster_id)
    JOIN ${this.tables.finalCuration} fc
      ON fc.entity_id = CAST(t.single_cluster_id AS VARCHAR)
    WHERE c.entity_neighbor_type = @T
      AND c.entity_neighbor_domain = @D
      AND c.entity_id = @I
    GROUP BY 1, 2, 3
),
flattened_neighbors AS (
    SELECT ARRAY_AGG(CONCAT(entity_neighbor_type, '~', entity_neighbor_domain, '~', CAST(entity_neighbor_id AS VARCHAR))) AS flatten_neighbors_array
    FROM ${this.tables.flatten}
    WHERE entity_type = @T
      AND entity_domain = @D
      AND entity_id = @I
)
SELECT
    @T AS entity_type,
    @D AS entity_domain,
    @I AS entity_id,
    fn.flatten_neighbors_array,
    cd.cluster_list,
    cd.final_graph_map
FROM valid_cluster_data cd
CROSS JOIN flattened_neighbors fn`
        },
        sqlTemplateBigQuery() {
            return `WITH valid_cluster_data AS (
    SELECT
        @T AS entity_type,
        @D AS entity_domain,
        @I AS entity_id,

        ARRAY_AGG(CONCAT(c.cluster_type, '~', c.cluster_domain, '~', single_cluster_id)) AS cluster_list,

        ARRAY_AGG(STRUCT(
            CONCAT(c.cluster_type, '~', c.cluster_domain, '~', single_cluster_id) AS key,
            ARRAY(
                SELECT CONCAT(fc.entity_neighbor_type, '~', fc.entity_neighbor_domain, '~', n.id)
                FROM UNNEST(fc.entity_neighbors) AS n
            ) AS value
        )) AS final_graph_map

    FROM ${this.tables.chooseCluster} c
    CROSS JOIN UNNEST(c.clusters) AS single_cluster_id
    JOIN ${this.tables.finalCuration} fc
      ON fc.entity_id = single_cluster_id
    WHERE c.neighbor_type = @T
      AND c.neighbor_domain = @D
      AND c.entity_id = @I
)
SELECT
    @T AS entity_type,
    @D AS entity_domain,
    @I AS entity_id,
    (
        SELECT ARRAY_AGG(CONCAT(entity_neighbor_type, '~', entity_neighbor_domain, '~', neighbor_id))
        FROM ${this.tables.flatten}
        WHERE entity_type = @T
          AND entity_domain = @D
          AND entity_id = @I
    ) AS flatten_neighbors_array,
    cd.cluster_list,
    cd.final_graph_map
FROM valid_cluster_data cd`
        },
        /**
         * What actually goes to Pail. Its athena -> bigquery translation step
         * rejects the pretty-printed form, so the query is collapsed onto one
         * line with no trailing semicolon — the same shape every other query on
         * the Graph Curation page uses. Whitespace is collapsed before the
         * literals go in, so an ID containing spaces is never rewritten.
         */
        sqlForExecution() {
            return this.fillTemplate(this.sqlTemplate.replace(/\s+/g, ' ').trim())
        },
        /** Full model — drives the breakdown panel and the stat strip. */
        model() {
            if (!this.row) return null
            return buildClusterFormationGraph(this.row, {
                mode: this.mode,
                expandCap: this.expandCap,
                filter: this.filter,
                moves: this.moves,
            })
        },
        /**
         * Why a drag cannot land anywhere on the graph as drawn, or '' when it can.
         * A member can only move to a cluster other than its own, so it takes two
         * clusters on screen — or one plus neighbours that are in no cluster.
         */
        moveBlockedReason() {
            const g = this.displayGraph
            if (!g) return ''
            const clusters = g.nodes.filter((n) => n.type === 'cluster').length
            const unclustered = g.nodes.some((n) => n.isOwnOnly)
            if (clusters >= 2 || (clusters === 1 && unclustered)) return ''
            if (this.focusCluster) {
                return 'Only the focused cluster is drawn, so there is no other cluster to drop onto. Click "Show all" in the Formation panel.'
            }
            if (!clusters) return 'This entity is in no cluster, so there is nothing to move members onto.'
            return 'This entity belongs to a single cluster and has no neighbours outside it, so there is no other cluster to move members to.'
        },
        /** client_user can trace a formation; every higher role can move members and save. */
        canCurate() {
            return this.$store.getters.userRoles.userRoleRank < 4
        },
        /** Members per cluster as the query returned them — the baseline moves are made against. */
        membership() {
            return this.row ? clusterMembership(this.row) : new Map()
        },
        /** Staged moves for the table, with cluster labels in the side panel's order. */
        moveRows() {
            const order = new Map(((this.model && this.model.clusters) || []).map((c, i) => [c.key, i + 1]))
            const label = (key) => {
                if (!key) return 'Separate node'
                const n = order.get(key)
                return n ? `Cluster ${n} · ${clusterIdOfKey(key)}` : clusterIdOfKey(key)
            }
            return this.moves.map((m) => ({
                ...m,
                rowKey: `${m.from}\0${m.key}`,
                fromLabel: label(m.from),
                toLabel: label(m.to),
            }))
        },
        /** Model narrowed to a single cluster when one is selected in the panel. */
        displayGraph() {
            const m = this.model
            if (!m) return null
            if (!this.focusCluster) return { nodes: m.nodes, edges: m.edges }

            const clusterId = `cluster:${this.focusCluster}`
            const keep = new Set(['root', clusterId])
            m.edges.forEach((e) => {
                if (e.source === clusterId) keep.add(e.target)
            })
            const nodes = m.nodes.filter((n) => keep.has(n.id))
            const edges = m.edges.filter((e) => keep.has(e.source) && keep.has(e.target))
            return { nodes, edges }
        },
        /** One plain sentence describing what the graph is showing. */
        explainer() {
            const m = this.model
            if (!m) return ''
            const b = (t) => `<b>${t}</b>`
            const n = m.stats.uniqueNeighbors
            const shared = m.stats.uniqueShared
            const clusters = m.stats.clusters

            /* Neighbours no cluster holds are drawn too, so the sentence has to
               account for them — otherwise "Your neighbours" on the stat strip
               never adds up against the picture. */
            const only = m.directOnlyCount
            const own = only
                ? ` ${b(only)} of your neighbour${only === 1 ? ' is' : 's are'} drawn as ` +
                  `separate node${only === 1 ? '' : 's'} off your entity.`
                : ''

            if (!clusters) {
                if (!m.stats.flatten) return `${b(m.rootKey)} is not in any cluster, and has no neighbours of its own.`
                return (
                    `${b(m.rootKey)} is not in any cluster. Its ${b(m.stats.flatten)} ` +
                    `neighbour${m.stats.flatten === 1 ? '' : 's'} ${m.stats.flatten === 1 ? 'is' : 'are'} ` +
                    `drawn off it directly.`
                )
            }

            const head =
                `${b(m.rootKey)} has ${b(m.stats.flatten)} neighbour${m.stats.flatten === 1 ? '' : 's'} of its own, ` +
                `and belongs to ${b(clusters)} cluster${clusters === 1 ? '' : 's'} holding ` +
                `${b(n)} neighbour${n === 1 ? '' : 's'} between them.`

            if (!shared) {
                return (
                    `${head} None of them is a direct neighbour of your entity, so nothing in this ` +
                    `data explains how they are linked.${own}`
                )
            }
            const tail =
                `${b(shared)} of those ${shared === 1 ? 'is' : 'are'} also a direct neighbour of your ` +
                `entity — that is what connects them, and the graph below draws ` +
                `${shared === 1 ? 'it' : 'them'} with a dashed line back to your entity.`
            return `${head} ${tail}${own}`
        },
        /** IDs listed under "Separate nodes" in the side panel. */
        ownPreview() {
            const ids = (this.model && this.model.directOnly) || []
            return this.ownShowAll ? ids : ids.slice(0, OWN_PREVIEW)
        },
        maxClusterSize() {
            const m = this.model
            if (!m || !m.clusters.length) return 1
            return Math.max(1, ...m.clusters.map((c) => c.neighborCount))
        },
    },
    watch: {
        /* A lookup and its staged moves belong to the cid they were made under —
           saving them after a client switch would write them to the wrong cid. */
        cid(next, prev) {
            if (!prev || next === prev) return
            this.row = null
            this.moves = []
            this.error = null
            this.focusCluster = null
        },
        filterInput(v) {
            clearTimeout(this.filterTimer)
            this.filterTimer = setTimeout(() => {
                this.filter = v
            }, 260)
        },
        expandCap(v) {
            const n = Number(v)
            if (!Number.isFinite(n)) return
            if (n > NEIGHBOR_EXPAND_CAP_MAX) this.expandCap = NEIGHBOR_EXPAND_CAP_MAX
            else if (n < 1) this.expandCap = 1
        },
    },
    mounted() {
        this.loadRecent()
        const fromUrl = this.$route.query.entity
        if (fromUrl) {
            this.entityInput = String(fromUrl)
            if (this.parsedEntity) this.runLookup()
        }
    },
    beforeUnmount() {
        clearTimeout(this.filterTimer)
    },
    methods: {
        onNodeMoved({ keys, fromCluster, toCluster }) {
            if (!this.canCurate) return
            this.moves = addClusterMove(this.moves, { keys, from: fromCluster, to: toCluster }, this.membership)
        },
        removeMove(row) {
            this.moves = this.moves.filter((m) => !(m.from === row.from && m.key === row.key))
        },
        clearMoves() {
            this.moves = []
        },
        async saveMoves() {
            if (!this.canCurate || !this.moves.length || !this.cid || this.savingMoves) return
            this.savingMoves = true
            const body = buildClusterFormationCurationPayload(this.moves)
            const url = `${this.$store.state.graphServiceUrl}/v1/cids/${encodeURIComponent(this.cid)}/curation/truthset?dataset=merge`
            try {
                await axios.post(url, body, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                const n = this.moves.length
                this.moves = []
                this.$store.dispatch('addToast', {
                    type: 'success',
                    message: `Saved ${n} move${n === 1 ? '' : 's'}. The clusters update after the next curation run.`,
                })
            } catch (e) {
                const msg = e.response?.data?.message || e.response?.data?.error || e.message || 'Unknown error'
                this.$store.dispatch('addToast', { type: 'error', message: `Saving moves failed: ${msg}` })
            } finally {
                this.savingMoves = false
            }
        },
        /** Substitute the three params into a template, escaping each literal. */
        fillTemplate(template) {
            const e = this.parsedEntity || { type: 'entity_type', domain: 'entity_domain', id: 'entity_id' }
            /* Single pass — chained replaces would rescan an already-inserted
               value, and IDs here are free text (emails, and so on). */
            const v = { '@T': lit(e.type), '@D': lit(e.domain), '@I': lit(e.id) }
            return template.replace(/@[TDI]/g, (m) => v[m])
        },
        barWidth(n) {
            const pct = Math.max(3, Math.round((n / this.maxClusterSize) * 100))
            return `${Math.min(100, pct)}%`
        },
        loadRecent() {
            try {
                const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
                if (Array.isArray(raw)) this.recentLookups = raw.filter((x) => typeof x === 'string').slice(0, RECENT_MAX)
            } catch (_) {
                this.recentLookups = []
            }
        },
        pushRecent(key) {
            const next = [key, ...this.recentLookups.filter((x) => x !== key)].slice(0, RECENT_MAX)
            this.recentLookups = next
            try {
                localStorage.setItem(RECENT_KEY, JSON.stringify(next))
            } catch (_) {
                /* storage unavailable — the list is a convenience only */
            }
        },
        sleep(ms) {
            return new Promise((r) => setTimeout(r, ms))
        },
        /** Column-info + data rows -> plain objects, matching the Graph Curation page. */
        rowsFromResult(result) {
            if (!result?.column_info || !result?.data) return []
            return result.data.map((row) => {
                const o = {}
                if (Array.isArray(row)) {
                    result.column_info.forEach((c, i) => {
                        o[c.name] = row[i]
                    })
                } else if (row && typeof row === 'object') {
                    result.column_info.forEach((c) => {
                        o[c.name] = row[c.name]
                    })
                }
                return o
            })
        },
        async runPailQuery(query) {
            if (!this.cid) throw new Error('No client selected.')
            const base = `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.cid}`
            const headers = {
                Authorization: `Bearer ${this.$store.state.accessToken}`,
                'Content-Type': 'application/json',
            }
            const url = `${base}/query_executions?source_dialect=${encodeURIComponent(this.queryDialect)}`
            const post = await axios.post(url, { query }, { headers })
            const qid = post.data?.query_execution_id
            if (!qid) throw new Error('No query_execution_id returned from Pail.')

            await this.sleep(800)
            for (let i = 0; i < MAX_POLL; i++) {
                const statusRes = await axios.get(`${base}/query_status/${qid}`, {
                    headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                })
                const st = statusRes.data
                if (st.State === 'FAILED') throw new Error(st.StateChangeReason || 'Query failed.')
                if (st.State && st.State !== 'QUEUED' && st.State !== 'RUNNING') {
                    const res = await axios.get(`${base}/query_executions/${qid}`, {
                        headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                    })
                    return res.data
                }
                await this.sleep(POLL_MS)
            }
            throw new Error('Query timed out.')
        },
        async runLookup() {
            const e = this.parsedEntity
            if (!e || this.loading) return

            this.loading = true
            this.error = null
            this.focusCluster = null

            const key = `${e.type}~${e.domain}~${e.id}`
            if (this.$route.query.entity !== key) {
                this.$router.replace({ query: { ...this.$route.query, entity: key } }).catch(() => {})
            }

            try {
                const result = await this.runPailQuery(this.sqlForExecution)
                const rows = this.rowsFromResult(result)
                if (!rows.length) {
                    this.row = null
                    this.error = `No cluster formation data found for ${key}.`
                    return
                }
                /* The query aggregates to a single row per entity; if a backend
                   change ever returns more, merge them so nothing is dropped. */
                this.row = rows.length === 1 ? rows[0] : this.mergeRows(rows, e)
                this.moves = []
                this.pushRecent(key)
            } catch (err) {
                this.row = null
                const body = err?.response?.data
                this.error = body?.error || body?.message || err?.message || 'Cluster formation query failed.'
                this.$store.dispatch('addToast', { type: 'error', message: this.error })
            } finally {
                this.loading = false
            }
        },
        mergeRows(rows, e) {
            return {
                entity_type: e.type,
                entity_domain: e.domain,
                entity_id: e.id,
                flatten_neighbors_array: rows.map((r) => r.flatten_neighbors_array),
                cluster_list: rows.map((r) => r.cluster_list),
                final_graph_map: rows.map((r) => r.final_graph_map),
            }
        },
    },
}
</script>

<style scoped>
.cluster-formation-page {
    --cf-teal: #0f766e;
    --cf-amber: #d97706;
}

/* ── Lookup bar ───────────────────────────────────────────────────── */
.cf-lookup-card {
    border-radius: 12px;
    margin-bottom: 14px;
}
.cf-lookup-body {
    padding: 14px 16px;
}
.cf-lookup-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    flex-wrap: wrap;
}
.cf-lookup-field {
    flex: 1 1 380px;
    min-width: 0;
}
.cf-field-label {
    display: block;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 5px;
}
.cf-input-shell {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #dde3ec;
    border-radius: 9px;
    padding: 0 10px;
    background: #fff;
    transition: border-color 0.14s, box-shadow 0.14s;
}
.cf-input-shell:focus-within {
    border-color: var(--cf-teal);
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
}
.cf-input-shell.is-invalid {
    border-color: #f0a48a;
}
.cf-input-icon {
    color: #94a3b8;
    font-size: 12px;
}
.cf-input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    padding: 9px 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    color: #0f172a;
    letter-spacing: -0.01em;
}
.cf-input::placeholder {
    color: #b6bfcc;
    letter-spacing: 0;
}
.cf-input-clear {
    border: 0;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    font-size: 12px;
    padding: 2px 4px;
}
.cf-input-clear:hover {
    color: #0f172a;
}

.cf-parse-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 7px;
    min-height: 21px;
}
.cf-parse-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    background: #f1f6f5;
    border: 1px solid #dbeae7;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 11px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #115e59;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cf-parse-chip i {
    font-style: normal;
    font-size: 8.5px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    opacity: 0.6;
}
.cf-parse-chip--id {
    flex: 0 1 auto;
    min-width: 0;
}
.cf-parse-hint {
    font-size: 11.5px;
    color: #94a3b8;
}
.cf-parse-hint--warn {
    color: #b45309;
}
.cf-parse-hint code {
    background: #f1f5f9;
    padding: 0 4px;
    border-radius: 3px;
}
.cf-example {
    border: 0;
    background: transparent;
    padding: 0;
    color: var(--cf-teal);
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
    cursor: pointer;
    text-decoration: underline dotted;
}

.cf-lookup-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-top: 21px;
}
.cf-run-btn {
    min-width: 148px;
}

.cf-recent-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 11px;
    padding-top: 11px;
    border-top: 1px dashed #eef1f5;
}
.cf-recent-label {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: #b6bfcc;
}
.cf-recent-chip {
    border: 1px solid #e6eaf0;
    background: #fff;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 10.5px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #64748b;
    cursor: pointer;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: all 0.14s;
}
.cf-recent-chip:hover {
    border-color: var(--cf-teal);
    color: var(--cf-teal);
}


/* ── Blank state ──────────────────────────────────────────────────── */
.cf-blank-card {
    border-radius: 12px;
}
.cf-blank {
    text-align: center;
    padding: 52px 24px 60px;
}
.cf-blank h5 {
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 8px;
}
.cf-blank p {
    max-width: 620px;
    margin: 0 auto;
    color: #7c8798;
    font-size: 13px;
    line-height: 1.7;
}
.cf-blank code {
    background: #f1f5f9;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 11.5px;
}
.cf-inline-bridge {
    color: #b45309;
    font-weight: 600;
    border-bottom: 2px dashed #d97706;
}
.cf-blank-art {
    position: relative;
    width: 118px;
    height: 118px;
    margin: 0 auto 22px;
}
.cf-blank-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px dashed #d7dee8;
}
.cf-blank-ring--1 {
    inset: 26px;
    border-style: solid;
    border-color: #e6eaf0;
}
.cf-blank-ring--2 {
    animation: cfSpin 26s linear infinite;
}
.cf-blank-core {
    position: absolute;
    inset: 44px;
    border-radius: 50%;
    background: var(--cf-teal);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    box-shadow: 0 0 0 6px rgba(15, 118, 110, 0.1);
}
@keyframes cfSpin {
    to {
        transform: rotate(360deg);
    }
}
@media (prefers-reduced-motion: reduce) {
    .cf-blank-ring--2 {
        animation: none;
    }
}

/* ── Stat strip ───────────────────────────────────────────────────── */
.cf-stat-strip {
    display: flex;
    align-items: stretch;
    flex-wrap: wrap;
    gap: 0;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    padding: 11px 4px;
    margin-bottom: 10px;
}
.cf-stat {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 2px 18px;
    border-left: 1px solid #eef1f5;
    min-width: 0;
}
.cf-stat:first-child {
    border-left: 0;
    flex: 1 1 240px;
    min-width: 0;
}
.cf-stat--num {
    flex: 0 0 auto;
}
.cf-stat-lbl {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: #94a3b8;
}
.cf-stat-val {
    font-size: 19px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.025em;
    font-variant-numeric: tabular-nums;
    line-height: 1.15;
}
.cf-stat-val--mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-top: 3px;
}
.cf-own-card {
    padding: 9px 12px 10px;
    border: 1px solid #e6eaf0;
    border-left: 3px solid var(--cf-teal);
    border-radius: 8px;
    background: #fff;
}
.cf-own-note {
    font-size: 11.5px;
    line-height: 1.5;
    color: #64748b;
    margin: 0 0 7px;
}
.cf-own-note code {
    font-size: 10.5px;
    background: #f1f5f9;
    padding: 1px 4px;
    border-radius: 3px;
    color: #475569;
}
.cf-own-list {
    max-height: 190px;
    overflow-y: auto;
}
.cf-own-row {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 10.5px;
    color: #334155;
    padding: 3px 0;
    border-bottom: 1px solid #f1f5f9;
    word-break: break-all;
}
.cf-own-row:last-child {
    border-bottom: 0;
}
.cf-own-more {
    font-size: 11.5px;
    margin-top: 6px;
}
.cf-stat--own .cf-stat-val {
    color: #0f766e;
}
.cf-stat--bridge .cf-stat-val {
    color: #b45309;
}

/* ── Bench ────────────────────────────────────────────────────────── */
.cf-explainer {
    margin: 0 0 10px;
    padding: 11px 16px;
    background: #fff;
    border-radius: 10px;
    border-left: 3px solid var(--cf-teal);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    font-size: 13.5px;
    line-height: 1.65;
    color: #3d4757;
}
.cf-explainer :deep(b) {
    color: #0f172a;
    font-weight: 700;
}

.cf-bench-row {
    /* Several stacked clusters need real height, or fit-to-screen shrinks the
       captions until they cannot be read. */
    min-height: 760px;
}
.cf-side-card,
.cf-graph-card {
    border-radius: 12px;
    overflow: hidden;
}
.cf-side-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px 8px;
    min-height: unset;
}
.cf-side-header .card-title {
    font-size: 12.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
}
.cf-clear-focus {
    font-size: 11px;
    font-weight: 600;
    text-decoration: none;
    color: var(--cf-teal);
}
.cf-side-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 0 12px 14px;
    min-height: 0;
    max-height: 840px;
}

.cf-level-tag {
    display: inline-block;
    font-size: 8.5px;
    text-transform: uppercase;
    letter-spacing: 0.11em;
    font-weight: 700;
    color: #0d9488;
    margin-bottom: 5px;
}
.cf-root-card {
    background: linear-gradient(135deg, #f0fbf9 0%, #f7fbfa 100%);
    border: 1px solid #d5ece8;
    border-left: 3px solid var(--cf-teal);
    border-radius: 9px;
    padding: 10px 12px;
}
.cf-root-key {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11.5px;
    font-weight: 600;
    color: #134e4a;
    word-break: break-all;
    line-height: 1.5;
}
.cf-root-meta {
    margin-top: 5px;
    font-size: 10.5px;
    color: #5f8a85;
}
.cf-dot-sep {
    margin: 0 5px;
    opacity: 0.5;
}

.cf-side-sec-lbl {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: #94a3b8;
    margin: 16px 0 8px;
}
.cf-side-sec-cnt {
    background: #f1f5f9;
    color: #64748b;
    border-radius: 20px;
    padding: 1px 7px;
    font-size: 9.5px;
    letter-spacing: 0;
}
.cf-side-empty {
    font-size: 12px;
    color: #94a3b8;
    margin: 0;
}

.cf-cluster-card {
    display: block;
    width: 100%;
    text-align: left;
    background: #fff;
    border: 1px solid #e6eaf0;
    border-left: 3px solid #cbd5e1;
    border-radius: 9px;
    padding: 9px 11px;
    margin-bottom: 7px;
    cursor: pointer;
    transition: border-color 0.14s, box-shadow 0.14s, transform 0.14s;
}
.cf-cluster-card:hover {
    border-color: #b4bfd0;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.07);
    transform: translateY(-1px);
}
.cf-cluster-card.has-bridge {
    border-left-color: var(--cf-amber);
}
.cf-cluster-card.is-focused {
    border-color: #6f42c1;
    border-left-color: #6f42c1;
    box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.12);
}
.cf-cluster-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 3px;
}
.cf-cluster-td {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 10.5px;
    font-weight: 700;
    color: #6f42c1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cf-bridge-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: #fef3c7;
    color: #b45309;
    border-radius: 20px;
    padding: 1px 7px;
    font-size: 9.5px;
    font-weight: 700;
    flex-shrink: 0;
}
.cf-bridge-badge i {
    font-size: 8px;
}
.cf-cluster-id {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 10px;
    color: #64748b;
    word-break: break-all;
    line-height: 1.45;
}
.cf-cluster-bar {
    position: relative;
    height: 4px;
    border-radius: 3px;
    background: #eef1f5;
    margin: 7px 0 5px;
    overflow: hidden;
}
.cf-cluster-bar-fill {
    position: absolute;
    inset: 0 auto 0 0;
    background: linear-gradient(90deg, #8b5cf6, #6f42c1);
    border-radius: 3px;
}
.cf-cluster-bar-bridge {
    position: absolute;
    inset: 0 auto 0 0;
    background: var(--cf-amber);
    border-radius: 3px;
}
.cf-cluster-meta {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 10.5px;
    color: #64748b;
}
.cf-cluster-meta b {
    color: #0f172a;
    font-variant-numeric: tabular-nums;
}
.cf-muted {
    color: #b6bfcc;
}

/* ── Graph card ───────────────────────────────────────────────────── */
.cf-graph-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 10px 14px;
    border-bottom: 1px solid #e6eaf0;
}
.cf-ctrl {
    display: flex;
    align-items: center;
    gap: 6px;
}
.cf-ctrl-sep {
    width: 1px;
    align-self: stretch;
    background: #e6eaf0;
    margin: 0 2px;
}
.cf-ctrl--grow {
    flex: 1 1 200px;
    min-width: 160px;
    position: relative;
}
.cf-ctrl--grow .form-control {
    padding-left: 28px;
}
.cf-ctrl-icon {
    position: absolute;
    left: 10px;
    font-size: 10.5px;
    color: #b6bfcc;
    pointer-events: none;
}
.cf-ctrl-lbl {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    font-weight: 700;
    color: #94a3b8;
}
.cf-cap-input {
    width: 76px;
}

.cf-switch {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    user-select: none;
    margin: 0;
}
.cf-switch input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}
.cf-switch-track {
    width: 32px;
    height: 18px;
    border-radius: 20px;
    background: #dde3ec;
    position: relative;
    transition: background 0.16s;
    flex-shrink: 0;
}
.cf-switch-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.3);
    transition: transform 0.16s;
}
.cf-switch.is-on .cf-switch-track {
    background: var(--cf-amber);
}
.cf-switch.is-on .cf-switch-knob {
    transform: translateX(14px);
}
.cf-switch-lbl {
    font-size: 11.5px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
}
.cf-switch.is-on .cf-switch-lbl {
    color: #b45309;
}
.cf-switch input:focus-visible + .cf-switch-track {
    outline: 2px solid var(--cf-teal);
    outline-offset: 2px;
}

.cf-graph-body {
    flex: 1;
    min-height: 660px;
    display: flex;
    flex-direction: column;
}
/* ── Merge curation ───────────────────────────────────────────────── */
.cf-move-hint {
    padding: 7px 14px;
    font-size: 12px;
    color: #047857;
    background: #ecfdf5;
    border-bottom: 1px solid #d1fae5;
}
.cf-move-hint--warn {
    color: #92400e;
    background: #fffbeb;
    border-bottom-color: #fde68a;
}
.cf-moved-in {
    color: #047857;
    font-weight: 600;
}
.cf-moves-card {
    border-radius: 12px;
}
.cf-moves-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
}
.cf-moves-scroll {
    max-height: 280px;
    overflow-y: auto;
}
.cf-move-id {
    display: inline-block;
    max-width: 420px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
}
.cf-move-chip {
    display: inline-block;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f1f5f9;
    color: #475569;
    font-size: 11px;
}
.cf-move-chip--to {
    background: #ecfdf5;
    color: #047857;
}
.cf-move-chip--none {
    font-style: italic;
}

@media (max-width: 991.98px) {
    .cf-graph-body {
        min-height: 520px;
    }
    .cf-side-scroll {
        max-height: 340px;
    }
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .cf-field-label,
    .cf-stat-lbl,
    .cf-side-sec-lbl,
    .cf-ctrl-lbl,
    .cf-recent-label { color: $dark-text-muted; }

    .cf-input-shell {
        background: $dark-body-bg;
        border-color: $dark-border-color;
    }
    .cf-input { color: $dark-text-primary; }
    .cf-input::placeholder { color: $dark-text-muted; }
    .cf-input-clear:hover { color: $dark-text-primary; }

    .cf-parse-chip {
        background: #0d2b28;
        border-color: #17453f;
        color: #5eead4;
    }
    .cf-parse-hint { color: $dark-text-muted; }
    .cf-parse-hint code,
    .cf-blank code { background: $dark-body-bg; }
    .cf-example { color: #2dd4bf; }

    .cf-recent-row { border-top-color: $dark-border-color; }
    .cf-recent-chip {
        background: $dark-body-bg;
        border-color: $dark-border-color;
        color: $dark-text-muted;
    }
    .cf-recent-chip:hover { border-color: #2dd4bf; color: #2dd4bf; }

    .cf-blank h5 { color: $dark-text-primary; }
    .cf-blank p { color: $dark-text-muted; }
    .cf-blank-ring { border-color: $dark-border-color; }
    .cf-blank-ring--1 { border-color: $dark-border-color; }

    .cf-stat-strip { background: $dark-card-bg; }
    .cf-stat { border-left-color: $dark-border-color; }
    .cf-stat-val { color: $dark-text-primary; }
    .cf-stat--bridge .cf-stat-val { color: #fbbf24; }
    .cf-stat--own .cf-stat-val { color: #5eead4; }
    .cf-own-card { background: $dark-card-bg; border-color: $dark-border-color; }
    .cf-own-note { color: $dark-text-muted; }
    .cf-own-note code { background: $dark-body-bg; color: $dark-text-primary; }
    .cf-own-row { color: $dark-text-primary; border-bottom-color: $dark-border-color; }

    .cf-root-card {
        background: linear-gradient(135deg, #0d2b28 0%, #12211f 100%);
        border-color: #17453f;
        border-left-color: #14b8a6;
    }
    .cf-root-key { color: #99f6e4; }
    .cf-root-meta { color: #5eead4; opacity: 0.8; }
    .cf-level-tag { color: #2dd4bf; }
    .cf-side-sec-cnt { background: $dark-border-color; color: $dark-text-muted; }
    .cf-side-empty { color: $dark-text-muted; }
    .cf-clear-focus { color: #2dd4bf; }

    .cf-cluster-card {
        background: $dark-body-bg;
        border-color: $dark-border-color;
        border-left-color: #3d4552;
    }
    .cf-cluster-card:hover { border-color: #4a5568; }
    .cf-cluster-card.has-bridge { border-left-color: #d97706; }
    .cf-cluster-card.is-focused { border-color: #a78bfa; border-left-color: #a78bfa; }
    .cf-cluster-td { color: #c4b5fd; }
    .cf-cluster-id { color: $dark-text-muted; }
    .cf-cluster-bar { background: $dark-border-color; }
    .cf-cluster-meta { color: $dark-text-muted; }
    .cf-cluster-meta b { color: $dark-text-primary; }
    .cf-bridge-badge { background: #451a03; color: #fbbf24; }
    .cf-muted { color: #4a5568; }

    .cf-graph-controls { border-bottom-color: $dark-border-color; }
    .cf-move-hint { background: #052e22; border-bottom-color: #064e3b; color: #6ee7b7; }
    .cf-move-hint--warn { background: #2a1d05; border-bottom-color: #713f12; color: #fcd34d; }
    .cf-moved-in { color: #6ee7b7; }
    .cf-move-chip { background: $dark-body-bg; color: $dark-text-muted; }
    .cf-move-chip--to { background: #052e22; color: #6ee7b7; }
    .cf-ctrl-sep { background: $dark-border-color; }
    .cf-explainer {
        background: $dark-card-bg;
        border-left-color: #14b8a6;
        color: $dark-text-muted;
    }
    .cf-explainer b { color: $dark-text-primary; }
    .cf-switch-track { background: $dark-border-color; }
    .cf-switch-lbl { color: $dark-text-muted; }
    .cf-switch.is-on .cf-switch-lbl { color: #fbbf24; }
}
</style>
